import React from 'react';
import { useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import type { WithPlatformDefaults } from '@lifeomic/react-client';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';

import { MemberDetailPanel } from '../../RegenerativeMedicineOps/components/MemberDetailPanel';
import { regenerativeMedicineOpsMessages } from '../../RegenerativeMedicineOps/messages';

const useStyles = makeStyles(theme => ({
  pageContent: {
    flex: 1,
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
}));

type RegenerativeMedicineOpsContentProps = {
  member: WithPlatformDefaults<fhir3.Patient>;
  memberId: string;
  fallback: React.ReactNode;
};

export const RegenerativeMedicineOpsContent: React.FC<
  RegenerativeMedicineOpsContentProps
> = ({ member, memberId, fallback }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const detailQuery = apiHooks.useAPIQuery(
    'GET /v1/member-operations/regenerative-medicine/members/:patientId',
    { patientId: memberId },
    {
      staleTime: 0,
      refetchOnWindowFocus: false,
      enabled: Boolean(memberId) && Boolean(member),
    },
  );

  if (!member) {
    return <>{fallback}</>;
  }

  if (detailQuery.isLoading) {
    return (
      <Box fullWidth fullHeight align="center" justify="center" gap={1}>
        <DotLoader />
      </Box>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Box fullWidth fullHeight align="center" justify="center">
        <Text size="subbody">
          {formatMessage(
            regenerativeMedicineOpsMessages.memberDetailUnavailable,
          )}
        </Text>
      </Box>
    );
  }

  return (
    <Box className={classes.pageContent}>
      <MemberDetailPanel detail={detailQuery.data} showMemberLink={false} />
    </Box>
  );
};
